import { useTranslation } from 'react-i18next'
import { FormEvent, useState } from 'react'
import { ZodIssue } from 'zod'
import { FormErrorsAlert } from '@/shared/components'
import { Button, InputGroup } from '@/common'
import { changePasswordSchema } from '@/features/auth/api/AuthModel'
import toast from 'react-hot-toast'
import { AxiosError } from 'axios'
import { useChangePassword, useGetUserSession } from '@/features/auth/hooks/useAuth'
import AuthLayout from '@/features/auth/components/AuthLayout'

export default function ChangePassword() {
  const { t } = useTranslation()
  const { refetch: fetchSession } = useGetUserSession()
  const [errors, setErrors] = useState<ZodIssue[]>([])
  const [currentPassword, setCurrentPassword] = useState<string>('')
  const [newPassword, setNewPassword] = useState<string>('')
  const [newPasswordRepeat, setNewPasswordRepeat] = useState<string>('')
  const { mutateAsync, isPending } = useChangePassword()
  const onSubmit = async (evt: FormEvent<HTMLFormElement>) => {
    evt.preventDefault()
    setErrors([])

    const validationResult = changePasswordSchema.safeParse({
      oldPassword: currentPassword,
      newPassword,
      newPasswordRepeat
    })

    if (!validationResult.success) {
      setErrors(validationResult.error.issues)
    } else {
      try {
        await mutateAsync({
          oldPassword: currentPassword,
          newPassword,
          newPasswordRepeat
        })
        fetchSession()
      } catch (err) {
        if (err instanceof AxiosError && err.response?.status === 401) {
          toast.error(t('login.errors.unauthorized'))
        } else {
          toast.error(t('login.errors.general'))
        }
      }
    }
  }

  return (
    <AuthLayout>
      <div className="items-center space-y-4 p-6">
        <div className="space-y-2 text-center">
          <p className="text-sm text-gray-600 font-medium">
            {t('common:changePassword.description')}
          </p>
        </div>

        <div>
          <FormErrorsAlert errors={errors} stringKeysPrefix="common:changePassword.fields" />
        </div>
        <form onSubmit={evt => onSubmit(evt)}>
          <div className="space-y-6 mb-10">
            <InputGroup
              onChange={evt => setCurrentPassword(evt.target.value)}
              name="password"
              label={t('changePassword.fields.oldPassword.label')}
              type="password"
              value={currentPassword}
            />
            <InputGroup
              onChange={evt => setNewPassword(evt.target.value)}
              name="password"
              label={t('changePassword.fields.newPassword.label')}
              type="password"
              value={newPassword}
            />
            <InputGroup
              onChange={evt => setNewPasswordRepeat(evt.target.value)}
              name="password"
              label={t('changePassword.fields.newPasswordRepeat.label')}
              type="password"
              value={newPasswordRepeat}
            />
          </div>
          <Button
            isLoading={isPending}
            disabled={currentPassword === '' || newPassword === '' || newPasswordRepeat === ''}
            className="px-4 py-2 rounded-lg w-full h-10 text-center"
            type="submit"
          >
            {t('changePassword.actions.submit')}
          </Button>
        </form>
      </div>
    </AuthLayout>
  )
}
